import { useEffect, useRef, type ReactNode, type Ref } from 'react'
import { Alert, Button, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { MessageBubble } from './MessageBubble'
import { CaseEventRow } from './CaseEventRow'
import { OperatorMessage } from './OperatorMessage'
import { Leaf } from './Leaf'
import { AttachedFiles } from './AttachedFiles'
import {
  AssistantMessageContent,
  parseAddTicketCommentResult,
  parseCreateTicketResult,
} from './AssistantMessageContent'
import { MessagesView } from './messages/MessagesView'
import { TypingIndicator } from './TypingIndicator'
import { WIDGET_REGISTRY } from './widgets/registry'
import { ChatInput, type ChatInputHandle } from './ChatInput'
import { WelcomeScreen } from './WelcomeScreen'
import {
  AnimatedScrollArea,
  type AnimatedScrollAreaHandle,
} from './AnimatedScrollArea'
import {
  AdviserRole,
  AdviserToolName,
  ToolInvocationState,
} from '../lib/constants'
import type {
  AttachedFile,
  CitationSource,
  ToolInvocation,
  FeedbackVote,
  HavenLaunchAction,
  WidgetDisplay,
  CustomerCaseEvent,
  OperatorIdentity,
} from '../lib/types'
import type { AttachmentRef } from '../lib/attachments.types'
import { parseListTicketsResult } from '../lib/parseListTickets'

interface ChatMessage {
  id: string
  role: `${AdviserRole}`
  content: string
  saved?: boolean
  textBeforeWidget?: Record<string, string>
  sourcesBeforeWidget?: Record<string, CitationSource[]>
  trailingContent?: string
  sources?: CitationSource[]
  toolInvocations?: ToolInvocation[]
  attachedFiles?: AttachedFile[]
  createdAt?: string
  operator?: OperatorIdentity | null
}

interface MessageListProps {
  messages: ChatMessage[]
  caseEvents?: CustomerCaseEvent[]
  hasOlderMessages?: boolean
  showAssistantAuthor?: boolean
  isStreaming?: boolean
  feedbackMap?: Record<string, FeedbackVote>
  onSend: (
    message: string,
    options?: {
      pendingAttachments?: AttachmentRef[]
      attachmentFiles?: File[]
    }
  ) => void
  onRetry?: () => void
  onFeedback?: (messageId: string, rating: -1 | 1, comment?: string) => void
  onWidgetOutcome?: (
    invocation: { toolCallId: string; toolName: string },
    text: string,
    display?: WidgetDisplay
  ) => Promise<boolean>
  messagesViewEnabled?: boolean
  liveMessagesView: ToolInvocation | null
}

/**
 * Only the newest messages list stays live: each one fetches on mount, and a
 * resumed conversation would otherwise replay every earlier one. A posted
 * reply counts as a list owner too, because the list reopens beneath it.
 */
const findLiveMessagesView = (
  messages: ChatMessage[]
): ToolInvocation | null => {
  for (let mi = messages.length - 1; mi >= 0; mi -= 1) {
    const owner = [...(messages[mi].toolInvocations ?? [])]
      .reverse()
      .find((inv) => {
        if (inv.state !== ToolInvocationState.OutputAvailable) return false

        switch (inv.toolName) {
          case AdviserToolName.ListTickets:
            return parseListTicketsResult(inv.result) !== null
          case AdviserToolName.AddTicketComment:
            return parseAddTicketCommentResult(inv.result) !== null
          default:
            return false
        }
      })
    if (owner) return owner
  }
  return null
}

const MessageList = ({
  messages,
  caseEvents = [],
  hasOlderMessages,
  showAssistantAuthor,
  isStreaming,
  feedbackMap,
  onSend,
  onRetry,
  onFeedback,
  onWidgetOutcome,
  messagesViewEnabled,
  liveMessagesView,
}: MessageListProps) => {
  // Track rendered tool results to prevent duplicates across messages
  const renderedTools = new Set<string>()
  // Find the most recent assistant message that holds a create_ticket
  // draft output. Only that draft is interactive; older ones render
  // as disabled read-only history.
  let latestDraftMessageIdx = -1
  for (let mi = messages.length - 1; mi >= 0; mi -= 1) {
    const invocations = messages[mi].toolInvocations ?? []
    const hasDraft = invocations.some((inv) => {
      if (
        inv.toolName !== AdviserToolName.CreateTicket ||
        inv.state !== ToolInvocationState.OutputAvailable
      ) {
        return false
      }
      const parsed = parseCreateTicketResult(inv.result)
      return parsed !== null && 'draft' in parsed && parsed.draft
    })
    if (hasDraft) {
      latestDraftMessageIdx = mi
      break
    }
  }

  const latestWidgetCallIds = new Set<string>()
  const widgetsSeen = new Set<string>()
  for (let mi = messages.length - 1; mi >= 0; mi -= 1) {
    const invocations = messages[mi].toolInvocations ?? []
    for (let ii = invocations.length - 1; ii >= 0; ii -= 1) {
      const { toolName, toolCallId } = invocations[ii]
      if (!WIDGET_REGISTRY[toolName] || !toolCallId) continue
      if (widgetsSeen.has(toolName)) continue
      widgetsSeen.add(toolName)
      latestWidgetCallIds.add(toolCallId)
    }
  }

  const oldestLoadedMessageAt = hasOlderMessages
    ? messages.reduce<string | null>((oldest, message) => {
        if (!message.createdAt) return oldest
        if (!oldest || message.createdAt < oldest) return message.createdAt
        return oldest
      }, null)
    : null
  const timeline = [
    ...messages.map((message, index) => ({
      kind: 'message' as const,
      at: message.createdAt ?? '\uffff',
      index,
    })),
    ...caseEvents
      .filter(
        (event) =>
          !hasOlderMessages ||
          (oldestLoadedMessageAt !== null && event.at >= oldestLoadedMessageAt)
      )
      .map((event) => ({
        kind: 'event' as const,
        at: event.at,
        event,
      })),
  ].sort((left, right) => {
    if (left.at !== right.at) return left.at < right.at ? -1 : 1
    if (left.kind !== right.kind) return left.kind === 'message' ? -1 : 1
    if (left.kind === 'message' && right.kind === 'message') {
      return left.index - right.index
    }
    if (left.kind === 'event' && right.kind === 'event') {
      return left.event.id - right.event.id
    }
    return 0
  })

  return (
    <>
      {timeline.map((entry) => {
        if (entry.kind === 'event') {
          return (
            <CaseEventRow
              key={`case-event-${entry.event.id}`}
              event={entry.event}
            />
          )
        }
        const i = entry.index
        const msg = messages[i]
        const isLast = i === messages.length - 1
        if (msg.role === AdviserRole.Operator) {
          return (
            <OperatorMessage
              key={msg.id}
              content={msg.content}
              operator={msg.operator ?? null}
            />
          )
        }
        const messageBubble = (
          <MessageBubble
            key={msg.id}
            role={msg.role}
            content={msg.content}
            isStreaming={
              isLast && isStreaming && msg.role === AdviserRole.Assistant
            }
          >
            {msg.role === AdviserRole.User && msg.attachedFiles && (
              <AttachedFiles files={msg.attachedFiles} />
            )}
            {msg.role === AdviserRole.Assistant && (
              <AssistantMessageContent
                messageId={msg.id}
                toolInvocations={msg.toolInvocations}
                messagesViewEnabled={messagesViewEnabled}
                liveMessagesView={liveMessagesView}
                textBeforeWidget={msg.textBeforeWidget}
                sourcesBeforeWidget={msg.sourcesBeforeWidget}
                hasText={msg.content.trim().length > 0}
                trailing={
                  msg.trailingContent ? (
                    <MessageBubble
                      role={msg.role}
                      content={msg.trailingContent}
                    />
                  ) : undefined
                }
                sources={msg.sources}
                isLatestDraft={i === latestDraftMessageIdx}
                isLatestMessage={isLast}
                latestWidgetCallIds={latestWidgetCallIds}
                renderedTools={renderedTools}
                isStreaming={isStreaming}
                streamingActive={isLast && isStreaming}
                feedbackMap={feedbackMap}
                onSend={onSend}
                onRetry={onRetry}
                onFeedback={onFeedback}
                onWidgetOutcome={onWidgetOutcome}
              />
            )}
          </MessageBubble>
        )
        if (msg.role === AdviserRole.Assistant && showAssistantAuthor) {
          return (
            <div key={msg.id} className="w-full max-w-[640px] self-start py-2">
              <div className="mb-2 flex items-center gap-2">
                <Leaf size={16} />
                <Text
                  variant={TypographyStyles.LABEL_SM}
                  color={TextColors.HEADER}
                >
                  Haven
                </Text>
              </div>
              {messageBubble}
            </div>
          )
        }
        return messageBubble
      })}
    </>
  )
}

interface ChatViewProps {
  messages: ChatMessage[]
  conversationId?: number | null
  caseEvents?: CustomerCaseEvent[]
  hasOlderMessages?: boolean
  hasOlderHistory?: boolean
  loadingOlderHistory?: boolean
  olderHistoryError?: boolean
  historyRevision?: number
  onLoadOlderHistory?: () => void
  showAssistantAuthor?: boolean
  isStreaming?: boolean
  error?: string | null
  firstName?: string
  /** Rendered above the transcript; scrolls away with it. */
  intro?: ReactNode
  /** The conversation opened from Messages, so it owes the member a list. */
  messagesLaunch?: boolean
  /** Rendered directly above the composer, inside its column. */
  composerStatus?: ReactNode
  onLaunchAction?: (action: HavenLaunchAction) => void
  messagesViewEnabled?: boolean
  inputPlaceholder?: string
  recipientHint?: string
  feedbackMap?: Record<string, FeedbackVote>
  hideInput?: boolean
  inputDisabled?: boolean
  inputRef?: Ref<ChatInputHandle>
  onSend: (
    message: string,
    options?: {
      pendingAttachments?: AttachmentRef[]
      attachmentFiles?: File[]
    }
  ) => void
  onRetry?: () => void
  onFeedback?: (messageId: string, rating: -1 | 1, comment?: string) => void
  onWidgetOutcome?: (
    invocation: { toolCallId: string; toolName: string },
    text: string,
    display?: WidgetDisplay
  ) => Promise<boolean>
  onScrolledChange?: (scrolled: boolean) => void
  /** Width of the message and input column, for a view whose scroller spans wider than the column. */
  contentClassName?: string
}

export const ChatView = ({
  messages,
  conversationId,
  caseEvents = [],
  hasOlderMessages = false,
  hasOlderHistory = false,
  loadingOlderHistory = false,
  olderHistoryError = false,
  historyRevision,
  onLoadOlderHistory,
  showAssistantAuthor,
  isStreaming,
  error,
  firstName,
  intro,
  messagesLaunch = false,
  composerStatus,
  onLaunchAction,
  messagesViewEnabled = false,
  inputPlaceholder,
  recipientHint,
  feedbackMap,
  hideInput,
  inputDisabled,
  inputRef,
  onSend,
  onRetry,
  onFeedback,
  onWidgetOutcome,
  onScrolledChange,
  contentClassName,
}: ChatViewProps) => {
  const scrollAreaRef = useRef<AnimatedScrollAreaHandle>(null)
  const newestHandoffId = caseEvents.reduce<number | null>(
    (latestId, event) =>
      event.kind === 'handoff'
        ? Math.max(latestId ?? event.id, event.id)
        : latestId,
    null
  )
  const previousHandoffId = useRef(newestHandoffId)
  const previousHistoryRevision = useRef(historyRevision)
  const handleLoadOlderHistory = () => {
    scrollAreaRef.current?.captureScrollPosition()
    onLoadOlderHistory?.()
  }

  const liveMessagesView = findLiveMessagesView(messages)
  // A Messages launch that settled without a list on screen is the member's
  // whole complaint, and nothing upstream can promise the model called the tool.
  const showLaunchMessagesList =
    messagesLaunch &&
    messagesViewEnabled &&
    !isStreaming &&
    messages.length > 0 &&
    liveMessagesView === null

  const lastMessage = messages[messages.length - 1]
  const latestSavedReplyId =
    messages
      .filter((message) => message.saved && message.role !== AdviserRole.User)
      .at(-1)?.id ?? null
  useEffect(() => {
    if (lastMessage?.role === AdviserRole.User) {
      scrollAreaRef.current?.scrollToBottom()
    }
  }, [messages.length, lastMessage?.role])

  useEffect(() => {
    // History reloads preserve scroll position; only a new handoff scrolls automatically.
    if (
      newestHandoffId !== null &&
      newestHandoffId !== previousHandoffId.current &&
      historyRevision === previousHistoryRevision.current
    ) {
      scrollAreaRef.current?.scrollToBottom()
    }
    previousHandoffId.current = newestHandoffId
    previousHistoryRevision.current = historyRevision
  }, [newestHandoffId, historyRevision])

  if (
    messages.length === 0 &&
    caseEvents.length === 0 &&
    !isStreaming &&
    !error
  ) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden bg-neutral-0">
        <div className="flex-1 min-h-0 flex flex-col">
          <WelcomeScreen
            firstName={firstName}
            input={
              hideInput ? undefined : (
                <ChatInput
                  ref={inputRef}
                  onSend={onSend}
                  placeholder={inputPlaceholder}
                  recipientHint={recipientHint}
                  disabled={inputDisabled}
                  initialFocus
                />
              )
            }
            illustrationClassName="[@media(max-height:650px)]:hidden"
            onSuggestionClick={onSend}
            onLaunchAction={onLaunchAction}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-neutral-0">
      <AnimatedScrollArea
        ref={scrollAreaRef}
        contentClassName={contentClassName}
        onScrolledChange={onScrolledChange}
        historyRevision={historyRevision}
        conversationId={conversationId}
        latestSavedReplyId={latestSavedReplyId}
      >
        {hasOlderHistory && (
          <div className="flex flex-col items-center gap-2 py-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={loadingOlderHistory}
              onClick={handleLoadOlderHistory}
            >
              {loadingOlderHistory
                ? 'Loading earlier history...'
                : 'Load earlier history'}
            </Button>
            {olderHistoryError && (
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.DANGER}
              >
                Earlier history could not load. Try again.
              </Text>
            )}
          </div>
        )}
        {intro}
        <MessageList
          messages={messages}
          caseEvents={caseEvents}
          hasOlderMessages={hasOlderMessages}
          showAssistantAuthor={showAssistantAuthor}
          messagesViewEnabled={messagesViewEnabled}
          isStreaming={isStreaming}
          feedbackMap={feedbackMap}
          onSend={onSend}
          onRetry={onRetry}
          onFeedback={onFeedback}
          onWidgetOutcome={onWidgetOutcome}
          liveMessagesView={liveMessagesView}
        />
        {showLaunchMessagesList && <MessagesView />}
        {isStreaming &&
          (messages.length === 0 ||
            messages[messages.length - 1]?.role === AdviserRole.User) && (
            <div className="py-2">
              <TypingIndicator />
            </div>
          )}
        {error && (
          <Alert
            variant="error"
            description={error}
            ctaText={onRetry ? 'Try again' : undefined}
            ctaOnClick={onRetry}
          />
        )}
      </AnimatedScrollArea>
      {!hideInput && (
        <div className="px-6 pb-2">
          <div className={contentClassName}>
            {composerStatus}
            <ChatInput
              onSend={onSend}
              placeholder={inputPlaceholder}
              recipientHint={recipientHint}
              disabled={isStreaming || inputDisabled}
              initialFocus
            />
          </div>
        </div>
      )}
    </div>
  )
}
